import { Button } from '@augmentt-dev/ui-components'
import { useFormikContext } from 'formik'
import PropTypes from 'prop-types'
import React, { useCallback, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { FormikMultiSelect, FormikSingleSelect, FormikToggleCheckbox } from '../../../../../components/FormikFields'
import useUserAccessTier from '../../../../../hooks/useUserAccessTier'
import { PW_MANAGER_SELECT_PROPS } from '../../../../../util/inputAttrs'
import { COMPANY_SELECTION_TYPES, getCompanySelectionTypeOptions } from '../../AddEditUserModal/utils'
import styles from './CompanyAccessFields.module.scss'

const CHECKBOX_CLASS_NAME = 'checkbox-left-aligned auto-z-index pt-0 mt-2'
const MAX_VISIBLE_COMPANIES = 5

/**
 * Company or collection access of a system user, bound to the surrounding Formik form.
 */
function CompanyAccessFields({
  companyAccessOptions,
  savedCollectionName = null,
  isOwner = false,
  isFetchingCustomerCollections = false,
  setCollectionToView,
}) {
  const intl = useIntl()
  const { values, initialValues, setFieldValue } = useFormikContext()
  const currentUser = useSelector(({ user }) => user?.data)
  const { isSocAdminUser } = useUserAccessTier()
  const {
    currentUserCollectionId,
    customerOptions,
    collectionOptions,
    collectionMap,
    allCustomersIds,
    allNonNfrCustomersIds,
  } = companyAccessOptions

  const companySelectionTypeOptions = useMemo(() => getCompanySelectionTypeOptions(intl), [intl])

  // Only a SOC admin assigns collections. Anyone else editing a user in a collection can keep that
  // collection or switch the user to companies, but cannot pick another collection.
  const savedCollectionId = initialValues.collectionId
  const canChangeSelectionType = isSocAdminUser || !!savedCollectionId

  // Collections only load for editors who can manage companies, so the saved one falls back to the
  // name the system users list provides.
  const getCollectionName = useCallback(
    (collectionId) => {
      const loadedName = collectionMap[collectionId]?.owner?.name
      if (loadedName) return loadedName
      return collectionId === savedCollectionId ? (savedCollectionName ?? '') : ''
    },
    [collectionMap, savedCollectionId, savedCollectionName],
  )

  const availableCollectionOptions = useMemo(() => {
    if (!savedCollectionId) return isSocAdminUser ? collectionOptions : []

    const savedCollectionOption = { value: savedCollectionId, label: getCollectionName(savedCollectionId) }
    if (!isSocAdminUser) return [savedCollectionOption]

    const isSavedCollectionLoaded = collectionOptions.some((option) => option.value === savedCollectionId)
    return isSavedCollectionLoaded ? collectionOptions : [savedCollectionOption, ...collectionOptions]
  }, [isSocAdminUser, collectionOptions, savedCollectionId, getCollectionName])

  const clearCompanySelection = useCallback(() => {
    setFieldValue('customers', [])
    setFieldValue('isAllCompanies', false)
    setFieldValue('excludeNFRCompany', false)
    setFieldValue('collectionId', null)
  }, [setFieldValue])

  const viewSelectedCollection = useCallback(
    () => setCollectionToView(collectionMap[values.collectionId]),
    [setCollectionToView, collectionMap, values.collectionId],
  )

  const toggleAllCompanies = useCallback(
    (checked) => {
      if (checked) {
        setFieldValue('customers', allCustomersIds)
      } else {
        setFieldValue('customers', [])
        setFieldValue('excludeNFRCompany', false)
      }
    },
    [setFieldValue, allCustomersIds],
  )

  const changeCompanies = useCallback(
    (value) => {
      setFieldValue('customers', value)
      setFieldValue('isAllCompanies', value?.length === customerOptions?.length)
    },
    [setFieldValue, customerOptions],
  )

  const toggleExcludeNfrCompany = useCallback(
    (checked) => setFieldValue('customers', checked ? allNonNfrCustomersIds : allCustomersIds),
    [setFieldValue, allNonNfrCustomersIds, allCustomersIds],
  )

  const nfrName = currentUserCollectionId
    ? collectionMap[currentUserCollectionId]?.owner?.name
    : currentUser?.Customer?.customer_name

  return (
    <>
      {canChangeSelectionType && (
        <FormikSingleSelect
          id="companySelectionType"
          className={styles.selectMenuUp}
          options={companySelectionTypeOptions}
          isClearable={false}
          isDisabled={isOwner}
          onChange={clearCompanySelection}
        />
      )}

      {values.companySelectionType === COMPANY_SELECTION_TYPES.COLLECTION && (
        <>
          <FormikSingleSelect
            id="collectionId"
            options={availableCollectionOptions}
            isLoading={isFetchingCustomerCollections}
            defaultLabel={intl.formatMessage({ id: 'GENERAL.NONE' })}
            title="GENERAL.COLLECTION"
          />

          {values.collectionId && (
            <>
              {collectionMap[values.collectionId] && (
                <Button
                  variant="link"
                  className={`p-0 ${styles.viewCollectionButton}`}
                  onClick={viewSelectedCollection}
                >
                  <FormattedMessage id="USERS.VIEW_COLLECTION" />
                </Button>
              )}
              <FormikToggleCheckbox
                id="excludeNFRCompany"
                label={
                  <FormattedMessage
                    id="ADMINISTRATION.EXCLUDE_NFR_ACCESS"
                    values={{ nfrName: getCollectionName(values.collectionId) }}
                  />
                }
                className={CHECKBOX_CLASS_NAME}
              />
            </>
          )}
        </>
      )}

      {values.companySelectionType === COMPANY_SELECTION_TYPES.COMPANY && (
        <>
          <div className="mb-2 d-flex align-items-baseline space-between">
            <span>
              <FormattedMessage id="ADMINISTRATION.COMPANIES" />
            </span>
            <FormikToggleCheckbox
              id="isAllCompanies"
              label={<FormattedMessage id="USERS.SELECT_ALL_COMPANIES" />}
              className={CHECKBOX_CLASS_NAME}
              disabled={isOwner}
              onChange={toggleAllCompanies}
            />
          </div>

          <FormikMultiSelect
            id="customers"
            options={customerOptions}
            isDisabled={isOwner}
            maxItems={MAX_VISIBLE_COMPANIES}
            {...PW_MANAGER_SELECT_PROPS}
            onChange={changeCompanies}
          />

          {values.isAllCompanies && (
            <FormikToggleCheckbox
              id="excludeNFRCompany"
              label={<FormattedMessage id="ADMINISTRATION.EXCLUDE_NFR_ACCESS" values={{ nfrName }} />}
              className={CHECKBOX_CLASS_NAME}
              disabled={isOwner}
              onChange={toggleExcludeNfrCompany}
            />
          )}
        </>
      )}
    </>
  )
}

const selectOptionPropType = PropTypes.shape({ value: PropTypes.number, label: PropTypes.string })

CompanyAccessFields.propTypes = {
  /** Result of useCompanyAccessOptions */
  companyAccessOptions: PropTypes.shape({
    currentUserCollectionId: PropTypes.number,
    customerOptions: PropTypes.arrayOf(selectOptionPropType),
    collectionOptions: PropTypes.arrayOf(selectOptionPropType),
    collectionMap: PropTypes.objectOf(PropTypes.shape({ owner: PropTypes.shape({ name: PropTypes.string }) })),
    allCustomersIds: PropTypes.arrayOf(PropTypes.number),
    allNonNfrCustomersIds: PropTypes.arrayOf(PropTypes.number),
  }).isRequired,
  /** Name of the user's saved collection, used when the collections list is not available */
  savedCollectionName: PropTypes.string,
  isOwner: PropTypes.bool,
  isFetchingCustomerCollections: PropTypes.bool,
  setCollectionToView: PropTypes.func.isRequired,
}

export default React.memo(CompanyAccessFields)
